import React, { useContext, useEffect, useState } from "react";
import { useRouter } from "next/router";
import { useDispatch } from "react-redux";
import { format, subDays } from "date-fns";
import { TextFieldV2 } from "components/V2/atoms/TextFieldV2/TextFieldV2";
import { CityAutocompleteTextboxV2 } from "components/V2/organisms/CityAutocompleteTextboxV2/CityAutocompleteTextboxV2";
import { CommonDataContext } from "context/CommonDataContext";
import { BOOKING_IS_ETIHAD_BOOKING, NOTIFICATION_MESSAGE_TYPE, STATUS, TYPE_OF_FLOW_IN_GROUP } from "../../../../../constants/variables";
import { BookingDataContext } from "../../../../../context/BookingDataContext";
import { BookingDetailContext } from "../../../../../context/BookingDetailsContext";
import { CurrentBookingInState } from "../../../../../dataLayer/CurrentBookingInState";
import { getBookingDetails, getBookingDetailsWithoutPNR } from "../../../../../dataLayer/manageBooking";
import { extractResource } from "../../../../../helper/extractResource";
import { displayNotificationMessage } from "../../../../../services/notificationMessagePopup";
import WithoutPnrValidation from "../../../../common/WithoutPnrValidation";
import { ButtonV2 } from "../../../atoms/ButtonV2/ButtonV2";
import { PricingCalendarV2 } from "../../PricingCalendarV2/PricingCalendarV2";
import { handleGroupBookingWithoutPNR } from "@/services/groupBooking/groupBooking";
import CheckInEtihadBookings from "../../CheckIn/CheckInEtihadBookingV2/checkInEtihadBookingRetrieve";

interface RetrieveBookingWithMobileFormV2Props {
  resourceData: {};
  mobileNumberPlaceholder: string;
  departureDatePlaceholder: string;
  fromPlaceholder: string;
  toPlaceholder: string;
  submitCTAText: string;
  setGClassOpenModal: (flag: boolean) => void;
}

export type AirportsInformation = {
  shortName: string;
  fullName: string;
  stationCode: string;
  cityInfo: {
    name: string;
  };
  currencyCode: string;
  locationDetails: {
    cityCode: string;
    countryCode: string;
    timeZoneCode: string;
    weightType: string;
    coordinates: {
      latitude: string;
      longitude: string;
      __typename: string;
    };
    zoneCode: string;
    subZoneCode: string;
    __typename: string;
  };
};

export function RetrieveBookingWithMobileFormV2({
  resourceData,
  mobileNumberPlaceholder,
  departureDatePlaceholder,
  fromPlaceholder,
  toPlaceholder,
  submitCTAText,
  setGClassOpenModal
}: RetrieveBookingWithMobileFormV2Props) {
  const { origin, setOrigin, destination, setDestination } = useContext(BookingDataContext);
  const [originCity, setOriginCity] = useState<string>("");
  const [destinationCity, setDestinationCity] = useState<string>("");

  const [originStationData, setOriginStationData] = useState<[]>([]);
  const [originStations, setOriginStations] = useState<[]>([]);
  const [destinationStationData, setDestinationStationData] = useState<[]>([]);
  const [destinationStations, setDestinationStations] = useState<[]>([]);
  const [originError, setOriginError] = useState("");
  const [destinationError, setDestinationError] = useState("");
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [formData, setFormData] = useState({
    phone: "",
    departureDate: format(new Date(), "yyyy/MM/dd"),
    origin: "",
    destination: ""
  });

  const [errors, setErrors] = useState<any>("");
  const [isLoader, setIsLoader] = useState(false);
  const { setCurrentBookingInStateData } = useContext(BookingDetailContext);
  const { popularAirports, selectedCurrency } = useContext<any>(CommonDataContext);
  const router = useRouter();
  const dispatch = useDispatch();

  const handleModalClose = () => {
    setIsModalOpen(false);
  };

  const handleFormChange = (e: any) => {
    if (e.target && e.target.name) {
      setFormData({
        ...formData,
        [e.target.name]: e.target.value
      });
    } else {
      setFormData({
        ...formData,
        departureDate: e
      });
    }
    if (formData.phone !== undefined && formData.phone.length > 10) {
      setErrors("");
    }
    if (formData.departureDate !== undefined) {
      setErrors("");
    }
    if (formData.origin !== undefined) {
      setErrors("");
    }
  };

  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setErrors(WithoutPnrValidation(formData));

    if (formData.phone && formData.departureDate && origin.stationCode && destination.stationCode) {
      setIsLoader(true);
      const params = {
        phone: `91${formData.phone}`,
        departureDate: formData.departureDate.replace(/\//g, '-'),
        destinationStatusCode: destination.stationCode,
        originStationCode: origin.stationCode
      };

      //Here We checking group booking.
      const paramGroupBooking = {
        phone: `91${formData.phone}`,
        departureDate: formData.departureDate.replace(/\//g, '-'),
        destinationStatusCode: destination.stationCode,
        originStationCode: origin.stationCode,
        bookingType: TYPE_OF_FLOW_IN_GROUP.MY_BOOKING,
        dispatch
      }

      const groupBookingHandled = await handleGroupBookingWithoutPNR(paramGroupBooking);

      if(groupBookingHandled === BOOKING_IS_ETIHAD_BOOKING){
        setIsModalOpen(true);
        setIsLoader(false);
        return;
      }

      if (groupBookingHandled && groupBookingHandled !== "NORMAL_BOOKING") {
        setGClassOpenModal(true); 
        setIsLoader(false);
        return;
      }

      if (groupBookingHandled === false) {
        setIsLoader(false);
        return;
      }

      //From here Normal booking is continue and for group booking it will return.
      
      let currentBookingState: any;
      const response: any = await getBookingDetailsWithoutPNR(params);
      if (response?.data?.data?.info) {
        const takeFirstBookingOnly = response?.data?.data;
        const reqParam = {
          pnrDetails: takeFirstBookingOnly?.recordLocator,
          lastName: takeFirstBookingOnly?.contacts[0]?.name?.last
        };
        await getBookingDetails(reqParam);
        currentBookingState = await CurrentBookingInState();

        const booking = currentBookingState?.data?.data;
        const isCancelled = (booking?.info?.status === "Closed" && booking?.journeys?.length === 0) ? true : false;
        if (isCancelled && response?.allowManageBooking === false) {
          displayNotificationMessage(NOTIFICATION_MESSAGE_TYPE.ERROR, extractResource(resourceData, "bookingCancelled"));
          setIsLoader(false);
          return;
        }

        if (currentBookingState?.data?.data?.restriction?.allowCheckIn === false ) {
          displayNotificationMessage("error", extractResource(resourceData, "checkIsNotAllowed")); 
          setIsLoader(false);
          return;
        }

        setCurrentBookingInStateData(currentBookingState); //update is BookingDetailContext
      }
      if (currentBookingState?.data?.data?.info?.status === STATUS.CONFIRMED) {
        setIsLoader(!isLoader);
        router.push("/manage-booking/retrieve-booking");
      } else if (currentBookingState?.data?.data?.info?.status === STATUS.CLOSED) {
        setIsLoader(false);
        displayNotificationMessage("error", extractResource(resourceData, "bookingCancelled"));
      } else {
        setIsLoader(false);
        displayNotificationMessage("error", extractResource(resourceData, "noBookingFound"));
      }
    }
  };

  const handleOriginCity = (selectedOrigin: AirportsInformation) => {
    setOrigin(selectedOrigin);
    setOriginCity(`${selectedOrigin?.cityInfo.name} (${selectedOrigin?.stationCode})`);
  };

  const handleDestinationCity = (selectedDestination: AirportsInformation) => {
    setDestination(selectedDestination);
    setDestinationCity(`${selectedDestination?.cityInfo.name} (${selectedDestination?.stationCode})`);
  };

  const getSelectedOrigin = (value: string) => {
    setOriginCity(value);
    if (value.length === 0) {
      setOriginStationData(originStationData);
    } else {
      const filteredOriginData: any = originStations?.filter(
        (originData: { shortName: any; stationCode: string; fullName: string }) =>
          JSON.stringify(originData?.shortName)?.toLowerCase().includes(value.toLowerCase()) ||
          JSON.stringify(originData?.fullName)?.toLowerCase().includes(value.toLowerCase()) ||
          JSON.stringify(originData?.stationCode)?.toLowerCase().includes(value.toLowerCase())
      );
      if (filteredOriginData?.length !== 0) {
        setOriginError("");
        setOriginStationData(filteredOriginData);
      } else {
        setOriginError(extractResource(resourceData, "errInvalidAirportSearch"));
        setOriginStationData([]);
      }
    }
  };
  const getSelectedDestination = (value: string) => {
    setDestinationCity(value);
    if (value.length === 0) {
      setDestinationStationData(originStationData);
    } else {
      const filteredDestinationData: any = destinationStations?.filter(
        (originData: { shortName: any; stationCode: string; fullName: string }) =>
          JSON.stringify(originData?.shortName)?.toLowerCase().includes(value.toLowerCase()) ||
          JSON.stringify(originData?.fullName)?.toLowerCase().includes(value.toLowerCase()) ||
          JSON.stringify(originData?.stationCode)?.toLowerCase().includes(value.toLowerCase())
      );
      if (filteredDestinationData?.length !== 0) {
        setDestinationStationData(filteredDestinationData);
        setDestinationError("");
      } else {
        setDestinationStationData([]);
        setDestinationError(extractResource(resourceData, "errInvalidAirportSearch"));
      }
    }
  };

  const handleDepartureDateClick = (date: Date) => {
    setFormData({ ...formData, departureDate: format(date, "yyyy/MM/dd") });
  };

  useEffect(() => {
    setOriginStationData(popularAirports || []);
    setOriginStations(popularAirports || []);
    setDestinationStationData(popularAirports || []);
    setDestinationStations(popularAirports || []);
  }, [popularAirports]);

  return (
    <form
      method="POST"
      onSubmit={handleSubmit}
      className="flex flex-col md:flex-row items-center justify-between w-full gap-2 space-y-2 md:space-y-0 py-6"
    >
      <div className="w-full h-[48px]">
        <TextFieldV2
          label={mobileNumberPlaceholder}
          type="number"
          name="phone"
          value={formData.phone}
          onChange={handleFormChange}
          maxLength={15}
          errorText={errors.phone && extractResource(resourceData, "invalidPhone")}
        />
      </div>
      <div className="w-full">
        <PricingCalendarV2
          label={departureDatePlaceholder}
          selectedOptionOrg={""}
          selectedOptionDest={""}
          selectedDate={String(formData.departureDate)}
          onChange={handleDepartureDateClick}
          minDate={String(subDays(new Date(), 180))}
          selectedCurrency={selectedCurrency}
        />
        {errors.departureDate && (
          <span className="text-xs font-normal text-typography-error my-1">
            {extractResource(resourceData, "chooseDepartureDate")}
          </span>
        )}
      </div>
      <div className="w-full h-[48px]">
        <CityAutocompleteTextboxV2
          errorText={originError}
          labelText={fromPlaceholder}
          value={originCity}
          onChange={getSelectedOrigin}
          suggestions={originStationData || []}
          onItemClick={handleOriginCity}
        />
      </div>

      <div className="w-full h-[48px]">
        <CityAutocompleteTextboxV2
          errorText={destinationError}
          labelText={toPlaceholder}
          value={destinationCity}
          onChange={getSelectedDestination}
          suggestions={destinationStationData || []}
          onItemClick={handleDestinationCity}
        />
      </div>
      <ButtonV2
        type="submit"
        color="primary"
        blok={{ text: submitCTAText, variant: "primary", isFullWidthOnMobile: true }}
        isLoading={isLoader}
        isDisabled={isLoader || !formData.phone || originError.length !== 0 || destinationError.length !== 0}
      />
      <CheckInEtihadBookings isOpen={isModalOpen} onClose={handleModalClose} flowType={TYPE_OF_FLOW_IN_GROUP.MY_BOOKING} />
    </form>
  );
}